import { useFormik } from 'formik';
import {  Button, Dialog, DialogContent, DialogTitle, Stack } from '@mui/material';
import { FormikTextInput } from '@coverright/ui/inputs';
import * as yup from "yup";
import * as React from 'react';
import { LoadingButton } from '@mui/lab';
import { useContext, useState } from 'react';
import { ModalCloseButton } from '@coverright/ui/shared';
import { useSaveEmailTemplateMutation } from '../../shared';
import { EmailTemplateDto } from '@coverright/data-access/types/enrollment';
import { RichEditorField } from '../../features';

const validationSchema = yup.object({
  title: yup.string().required('Field is required'),
  template: yup.string().required('Field is required'),
});

interface FormValues {
  title: string;
  template: string;
}

interface Props {
  initialValues?: FormValues,
  onSubmit: (values?: FormValues) => void,
  loading: boolean,
}

const Form = ({initialValues, onSubmit, loading}: Props) => {
  const formik = useFormik({
    initialValues: {
      title: initialValues?.title || '',
      template: initialValues?.template || '',
    },
    validationSchema: validationSchema,
    onSubmit: values => {
      if (formik.isValid) {
        onSubmit(values);
      }
    },
  });

  return  <Stack component={'form'} gap={2}
               onSubmit={formik.handleSubmit}>
    <FormikTextInput formik={formik} name={'title'} required />

    <RichEditorField value={formik.values.template} onChange={data => formik.setFieldValue('template', data)} />

    <Stack gap={2} direction={'row'} alignItems={'center'} justifyContent={'flex-end'}>
      <Button onClick={() => onSubmit()} variant={'outlined'}>
        Cancel
      </Button>
      <LoadingButton
        loading={loading}
        type={'submit'}
        variant={'contained'}
      >
        Save
      </LoadingButton>
    </Stack>

  </Stack>
}

interface ContextProps {
  open: (template?: EmailTemplateDto) => void;
}

const EmailTemplateFormContext = React.createContext<ContextProps>({
  open: () => {}
})

export const useEmailTemplateForm = () => useContext(EmailTemplateFormContext)

export function withEmailTemplateForm<P>(WrappedComponent: React.FC<P>) {
  return (props: P) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [template, setTemplate] = useState<EmailTemplateDto>();
    const [save, {loading}] = useSaveEmailTemplateMutation({
      refetchQueries: ['emailTemplates']
    })

    const open = (n?: EmailTemplateDto) => {
      setTemplate(n);
      setDialogOpen(true)
    }

    const handleSubmit = async (values?: FormValues) => {
      if (values) {
        try {
          await save({variables: {emailTemplate: {id: template?.id, ...values}}});
          setDialogOpen(false)
          setTimeout(() => setTemplate(undefined), 300);
        } catch (error) {}
      }
      setDialogOpen(false)
    }

    return <EmailTemplateFormContext.Provider value={{open}}>
      <Dialog fullWidth maxWidth={'sm'} open={dialogOpen} onClose={() => handleSubmit()}>
        <DialogTitle variant={'h3'}>
          {template ? 'Edit' : 'Create'} template
          {dialogOpen && <ModalCloseButton onClick={() => handleSubmit()} sx={{ position: 'absolute', top: 4, right: 8 }} />}
        </DialogTitle>
        <DialogContent>
          <Form loading={loading} initialValues={{
            title: template?.title || '',
            template: template?.template || '',
          }} onSubmit={handleSubmit} />
        </DialogContent>
      </Dialog>
      <WrappedComponent {...props} />
    </EmailTemplateFormContext.Provider>
  }
}
